import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import UnsavedChangesDialog from 'src/components/UnsavedChangesDialog';
import { forcedLogout } from 'src/contexts/AuthContext';

// Set true by a screen while it programmatically pops its own buffered history entry, so the guard
// treats that self-inflicted POP as a no-op instead of a user navigating away.
export const historyRollback = { active: false };

const UnsavedChangesContext = createContext({
  guard: (proceed) => proceed(),
  setDirty: () => {},
});

export function UnsavedChangesProvider({ children }) {
  const history = useHistory();
  const dirtyKeys = useRef(new Set());
  const saveHandlers = useRef(new Map());
  const descriptions = useRef(new Map());
  const pillButtonKeys = useRef(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [open, setOpen] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [description, setDescription] = useState(undefined);
  const [pillButtons, setPillButtons] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingProceed = useRef(null);
  const unblockRef = useRef(null);

  const setDirty = useCallback((key, dirty, onSave, screenDescription, pillStyle) => {
    const keys = dirtyKeys.current;
    if (dirty) {
      keys.add(key);
      if (onSave) saveHandlers.current.set(key, onSave);
      else saveHandlers.current.delete(key);
      if (screenDescription) descriptions.current.set(key, screenDescription);
      else descriptions.current.delete(key);
      if (pillStyle) pillButtonKeys.current.add(key);
      else pillButtonKeys.current.delete(key);
    } else {
      keys.delete(key);
      saveHandlers.current.delete(key);
      descriptions.current.delete(key);
      pillButtonKeys.current.delete(key);
    }
    setIsDirty(keys.size > 0);
  }, []);

  // Open the prompt, remembering how to continue, whether Save is available, and any screen message.
  const promptThen = useCallback((proceed) => {
    pendingProceed.current = proceed;
    setCanSave([...dirtyKeys.current].some((key) => saveHandlers.current.has(key)));
    setDescription([...dirtyKeys.current].map((key) => descriptions.current.get(key)).find(Boolean));
    setPillButtons([...dirtyKeys.current].some((key) => pillButtonKeys.current.has(key)));
    setOpen(true);
  }, []);

  // Run `proceed` straight away when clean; otherwise warn first.
  const guard = useCallback(
    (proceed) => {
      if (dirtyKeys.current.size === 0) {
        proceed();
        return;
      }
      promptThen(proceed);
    },
    [promptThen]
  );

  // Intercept in-app route changes while dirty and route them through the same prompt.
  useEffect(() => {
    if (!isDirty) return undefined;
    unblockRef.current = history.block((location) => {
      // Forced logout (expired session) tears down mid-edit — let it through, no prompt.
      // historyRollback: a screen popping its own buffered entry, not a real navigation.
      if (forcedLogout.active || historyRollback.active) return undefined;
      promptThen(() => {
        if (unblockRef.current) unblockRef.current();
        unblockRef.current = null;
        history.push(`${location.pathname}${location.search}${location.hash}`);
      });
      return false;
    });
    return () => {
      if (unblockRef.current) unblockRef.current();
      unblockRef.current = null;
    };
  }, [isDirty, history, promptThen]);

  // Browser-level exits (refresh / close / external nav) the in-app guard can't intercept.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const cancel = () => {
    pendingProceed.current = null;
    setOpen(false);
  };

  const discard = () => {
    const proceed = pendingProceed.current;
    pendingProceed.current = null;
    dirtyKeys.current.clear();
    saveHandlers.current.clear();
    descriptions.current.clear();
    pillButtonKeys.current.clear();
    setIsDirty(false);
    setOpen(false);
    proceed?.();
  };

  const save = async () => {
    const proceed = pendingProceed.current;
    const handlers = [...dirtyKeys.current].map((key) => saveHandlers.current.get(key)).filter(Boolean);
    setSaving(true);
    try {
      await Promise.all(handlers.map((handler) => handler()));
    } catch {
      setSaving(false);
      return;
    }
    pendingProceed.current = null;
    dirtyKeys.current.clear();
    saveHandlers.current.clear();
    descriptions.current.clear();
    pillButtonKeys.current.clear();
    setIsDirty(false);
    setSaving(false);
    setOpen(false);
    proceed?.();
  };

  const value = useMemo(() => ({ guard, setDirty }), [guard, setDirty]);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        open={open}
        description={description}
        pillButtons={pillButtons}
        onSave={canSave ? save : undefined}
        onDiscard={discard}
        onCancel={cancel}
        saving={saving}
      />
    </UnsavedChangesContext.Provider>
  );
}

UnsavedChangesProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

// Report a form's dirty state into the guard; auto-clears on unmount.
// Pass `onSave` to enable the prompt's Save button (must resolve once the save completes).
// Pass `description` to override the prompt's message for this screen.
// Pass `pillStyle` to render this screen's prompt with the lighter pill buttons.
export function useReportDirty(dirty, onSave, description, pillStyle) {
  const { setDirty } = useContext(UnsavedChangesContext);
  const idRef = useRef();
  if (!idRef.current) idRef.current = Symbol('dirty');
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  useEffect(() => {
    setDirty(idRef.current, dirty, onSaveRef.current ? () => onSaveRef.current() : undefined, description, pillStyle);
    return () => setDirty(idRef.current, false);
  }, [dirty, setDirty, description, pillStyle]);
}

// Bridge for Formik render-props, which can't call hooks directly.
export function DirtyReporter({ dirty, onSave, description, pillStyle }) {
  useReportDirty(dirty, onSave, description, pillStyle);
  return null;
}

DirtyReporter.propTypes = {
  dirty: PropTypes.bool,
  onSave: PropTypes.func,
  description: PropTypes.string,
  pillStyle: PropTypes.bool,
};

export default UnsavedChangesContext;
